import { useDynamicLinkListQuery } from "@saleor/banners/queries";
import CardSpacer from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import { Tab, TabContainer } from "@saleor/components/Tab";
import { BannerErrorFragment } from "@saleor/fragments/types/BannerErrorFragment";
import useStateFormProps from "@saleor/hooks/useStateFromProps";
import { maybe } from "@saleor/misc";
import { getChoices } from "@saleor/products/utils/data";
import { SearchMalls_search_edges_node } from "@saleor/searches/types/SearchMalls";
import { SearchSales_search_edges_node } from "@saleor/searches/types/SearchSales";
import { SearchVouchers_search_edges_node } from "@saleor/searches/types/SearchVouchers";
import { FetchMoreProps } from "@saleor/types";
import createMultiAutocompleteSelectHandler from "@saleor/utils/handlers/multiAutocompleteSelectChangeHandler";
import { useCreateBannerValidation } from "@saleor/utils/validators/banners";
import { hasNoError } from "@saleor/utils/validators/sales";
import { RawDraftContentState } from "draft-js";
import Moment from "moment";
import React from "react";
import { useIntl } from "react-intl";

import {
  BannerVerticalEnum,
  SmopBannerTypeEnum,
  SmopStatusEnum
} from "../../../types/globalTypes";
import BannerBrands from "../BannerBrands";
import BannerCategories from "../BannerCategories";
import BannerCollections from "../BannerCollections";
import BannerCouponCollections from "../BannerCouponCollections";
import BannerCoupons from "../BannerCoupons";
import BannerDateTime from "../BannerDateTime";
import { BannerDetailsPageTab } from "../BannerDetailsPage";
import BannerDynamicLinksVertical from "../BannerDynamicLinksVertical";
import BannerInfo from "../BannerInfo";
import BannerMallList from "../BannerMallList";
import BannerProducts from "../BannerProducts";
export interface BannerFormData {
  description: RawDraftContentState;
  endDate: string;
  endTime: string;
  image: string;
  isSplashBanner: boolean;
  mall: string;
  mallName: string;
  name: string;
  saleId: string;
  saleName: string;
  startDate: string;
  startTime: string;
  status: SmopStatusEnum;
  subDescription: string;
  type: SmopBannerTypeEnum;
  voucherId: string;
  voucherName: string;
  verticals: BannerVerticalEnum[];
}
export interface BannerCreatePageProps {
  activeTab: BannerDetailsPageTab;
  malls?: SearchMalls_search_edges_node[];
  disabled: boolean;
  errors: BannerErrorFragment[];
  sales?: SearchSales_search_edges_node[];
  saveButtonBarState: ConfirmButtonTransitionState;
  vouchers?: SearchVouchers_search_edges_node[];
  fetchMoreMalls: FetchMoreProps;
  onTabClick: (index: BannerDetailsPageTab) => void;
  onBack: () => void;
  fetchMalls: (query: string) => void;
  fetchSales: (query: string) => void;
  fetchVouchers: (query: string) => void;
  onSubmit: (data: BannerFormData) => void;
}
const BannerCreatePage: React.FC<BannerCreatePageProps> = ({
  activeTab,
  disabled,
  errors,
  fetchMoreMalls,
  malls,
  sales,
  saveButtonBarState,
  vouchers,
  fetchMalls,
  fetchSales,
  fetchVouchers,
  onTabClick,
  onSubmit,
  onBack
}) => {
  const intl = useIntl();

  const [validator, setValidator] = React.useState({
    endDate: null,
    endTime: null,
    name: null,
    startDate: null,
    startTime: null,
    verticals: null
  });

  const initialForm: BannerFormData = {
    description: {} as any,
    endDate: "",
    endTime: "",
    image: null,
    isSplashBanner: false,
    mall: "all",
    mallName: "All Malls",
    name: null,
    saleId: null,
    saleName: null,
    startDate: "",
    startTime: Moment(new Date().setUTCHours(0, 0, 0, 0)).format("HH:mm"),
    status: SmopStatusEnum.ACT,
    subDescription: "",
    type: SmopBannerTypeEnum.ANNOUNCEMENT,
    verticals: [],
    voucherId: null,
    voucherName: null
  };

  const featuredInChoices = [
    {
      label: "Homepage",
      value: BannerVerticalEnum.HOMEPAGE
    },
    {
      label: "Food",
      value: BannerVerticalEnum.FOOD
    },
    {
      label: "Grocery",
      value: BannerVerticalEnum.GROCERY
    },
    {
      label: "ShopSM",
      value: BannerVerticalEnum.SM_FULFILLED
    },
    {
      label: "Shops",
      value: BannerVerticalEnum.SHOPS
    },
    {
      label: "Cinema",
      value: BannerVerticalEnum.CINEMA
    },
    {
      label: "In Mall Deals",
      value: BannerVerticalEnum.IN_MALL_DEALS
    }
  ];

  const [selectedFeatured, setSelectedFeatured] = useStateFormProps(
    getChoices([])
  );

  const handleSubmit = event => {
    const validate = useCreateBannerValidation(event);
    setValidator(validate);
    if (hasNoError(validate)) {
      onSubmit(event);
    } else {
      setValidator(validate);
    }
  };

  const breadcrumbDirectory = [
    {
      name: "Banners",
      url: "/discounts/banners"
    },
    {
      name: "Create Banner"
    }
  ];

  const BrandTab = Tab(BannerDetailsPageTab.brands);
  const CollectionTab = Tab(BannerDetailsPageTab.collections);
  const ProductsTab = Tab(BannerDetailsPageTab.products);
  const CategoryTab = Tab(BannerDetailsPageTab.categories);
  const VerticalTab = Tab(BannerDetailsPageTab.verticals);
  const CouponTab = Tab(BannerDetailsPageTab.coupons);
  const CouponCollectionsTab = Tab(BannerDetailsPageTab.couponCollections);

  const { data: dynamicLinkList } = useDynamicLinkListQuery({
    displayLoader: true,
    variables: {
      first: 100
    }
  });

  return (
    <Form initial={initialForm} onSubmit={handleSubmit}>
      {({ data, change, hasChanged, submit, toggleValue }) => {
        const handleSelectFeatured = createMultiAutocompleteSelectHandler(
          toggleValue,
          setSelectedFeatured,
          selectedFeatured,
          featuredInChoices
        );
        return (
          <Container>
            <PageBreadcrumbNavigation items={breadcrumbDirectory} />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                marginTop: "10px"
              }}
            >
              <PageBackNavigation onBack={onBack} />
              <PageHeader
                title={intl.formatMessage({
                  defaultMessage: "Create Banner",
                  description: "page header"
                })}
              />
            </div>
            <div>
              <BannerInfo
                data={data}
                disabled={disabled}
                errors={errors}
                fetchMalls={fetchMalls}
                fetchMoreMalls={fetchMoreMalls}
                fetchSales={fetchSales}
                fetchVouchers={fetchVouchers}
                handleNameChange={change}
                isEdit={false}
                localError={validator}
                malls={malls}
                onChange={change}
                sales={sales}
                vouchers={vouchers}
                featuredInDisplayValue={selectedFeatured}
                featuredChoices={featuredInChoices}
                setSelectedFeatured={setSelectedFeatured}
                onFeaturedChange={handleSelectFeatured}
              />
              <CardSpacer />
              <BannerDateTime
                clientErrors={validator}
                disabled={
                  disabled || data.type !== SmopBannerTypeEnum.ANNOUNCEMENT
                }
                errors={errors}
                onChange={change}
                data={data}
              />
              <CardSpacer />
              {data.type === SmopBannerTypeEnum.ANNOUNCEMENT && (
                <BannerMallList
                  disabled={true}
                  malls={maybe(() => data.malls)}
                  emptyText="Assigned to all malls"
                  title="Malls"
                />
              )}
            </div>
            <CardSpacer />
            {data.type === SmopBannerTypeEnum.ANNOUNCEMENT && (
              <TabContainer>
                <ProductsTab
                  isActive={activeTab === BannerDetailsPageTab.products}
                  changeTab={onTabClick}
                >
                  {intl.formatMessage({
                    defaultMessage: "Product",
                    description: "product header"
                  })}
                </ProductsTab>

                <CollectionTab
                  isActive={activeTab === BannerDetailsPageTab.collections}
                  changeTab={onTabClick}
                >
                  {intl.formatMessage({
                    defaultMessage: "Product Collection",
                    description: "product collection header"
                  })}
                </CollectionTab>

                <BrandTab
                  isActive={activeTab === BannerDetailsPageTab.brands}
                  changeTab={onTabClick}
                >
                  {intl.formatMessage({
                    defaultMessage: "Brand",
                    description: "brand header"
                  })}
                </BrandTab>

                <CategoryTab
                  isActive={activeTab === BannerDetailsPageTab.categories}
                  changeTab={onTabClick}
                >
                  {intl.formatMessage({
                    defaultMessage: "Category",
                    description: "category header"
                  })}
                </CategoryTab>

                <VerticalTab
                  isActive={activeTab === BannerDetailsPageTab.verticals}
                  changeTab={onTabClick}
                >
                  {intl.formatMessage({
                    defaultMessage: "Vertical",
                    description: "vertical header"
                  })}
                </VerticalTab>

                <CouponTab
                  isActive={activeTab === BannerDetailsPageTab.coupons}
                  changeTab={onTabClick}
                >
                  {intl.formatMessage({
                    defaultMessage: "Coupon",
                    description: "coupon header"
                  })}
                </CouponTab>

                <CouponCollectionsTab
                  isActive={
                    activeTab === BannerDetailsPageTab.couponCollections
                  }
                  changeTab={onTabClick}
                >
                  {intl.formatMessage({
                    defaultMessage: "Coupon Collection",
                    description: "coupon collection tab header"
                  })}
                </CouponCollectionsTab>
              </TabContainer>
            )}
            {data.type === SmopBannerTypeEnum.ANNOUNCEMENT && (
              <>
                <CardSpacer />
                {activeTab === BannerDetailsPageTab.products && (
                  <BannerProducts
                    disabled={true}
                    product={maybe(() => data.product)}
                    onRowClick={() => undefined}
                  />
                )}
                {activeTab === BannerDetailsPageTab.collections && (
                  <BannerCollections
                    collection={maybe(() => data.collection)}
                    disabled={true}
                    onRowClick={() => undefined}
                  />
                )}
                {activeTab === BannerDetailsPageTab.brands && (
                  <BannerBrands
                    brand={maybe(() => data.brand)}
                    disabled={true}
                    onRowClick={() => undefined}
                  />
                )}
                {activeTab === BannerDetailsPageTab.categories && (
                  <BannerCategories
                    category={maybe(() => data.category)}
                    disabled={true}
                    onRowClick={() => undefined}
                  />
                )}
                {activeTab === BannerDetailsPageTab.verticals && (
                  <BannerDynamicLinksVertical
                    disabled={true}
                    submitdata={data}
                    onChange={change}
                    dynamicLinkList={maybe(
                      () =>
                        dynamicLinkList?.dynamicLinks?.edges.map(
                          edge => edge.node
                        ),
                      []
                    )}
                  />
                )}
                {activeTab === BannerDetailsPageTab.coupons && (
                  <BannerCoupons
                    coupon={maybe(() => data.coupon)}
                    disabled={true}
                  />
                )}
                {activeTab === BannerDetailsPageTab.couponCollections && (
                  <BannerCouponCollections
                    couponCollection={maybe(() => data.couponCollection)}
                    disabled={true}
                  />
                )}
              </>
            )}
            <SaveButtonBar
              disabled={disabled || !hasChanged}
              onCancel={onBack}
              onSave={submit}
              state={saveButtonBarState}
            />
          </Container>
        );
      }}
    </Form>
  );
};
BannerCreatePage.displayName = "BannerCreatePage";
export default BannerCreatePage;
